// apps/merchant-v3/src/services/settingsClient.ts
//
// Phase 6 Wave 11 Task 35 — Thin wrapper over `apiClient` for the
// merchant Settings surface (Profile + General + Activity Timeline).
//
// Endpoints:
//   PATCH /api/v1/auth/me                 — update name/phone/tz/lang/billing
//   POST  /api/v1/auth/me/avatar          — FormData avatar upload
//   GET   /api/v1/auth/me/activity        — last 30 actions (security log)
//   PATCH /api/v1/auth/me/preferences     — theme + density + locale + tz
//
// All endpoints unwrap the canonical success envelope via the api/client
// helper; on 404 / network failure the read endpoint (activity) returns
// an empty list so first paint never hard-fails (mirrors the wallet
// client pattern from Wave 9). Mutate endpoints surface ApiError so the
// page can render the inline form error.
//
// Phase 6 Wave 11 Task 36 — extends the client with the Security
// surface (password change, 2FA setup/disable, sessions, login history,
// forgot-password). Reuses the canonical 2FA verify endpoint already
// exposed by the wallet client (Wave 9 Task 30 — `verify2FA(code)`).
//
// Phase 6 Wave 11 Task 37 — extends the client with the Team Members
// surface (list/create/update/suspend/deactivate/reactivate/resend
// invite). All endpoints unwrap the canonical success envelope; the
// list endpoint falls back to an empty page on 404 / network failure
// so first paint never crashes (mirrors `getActivity()`). Mutate
// endpoints surface ApiError so the page can render the inline form
// error..
//
// Phase 6 Wave 11 Task 38 — extends the client with the
// `updateNotificationPrefs()` mutation backing the new
// SettingsNotificationsPage. Persists category × channel toggles +
// in-app sound flag.
//
// vg-binding: PLAN:task-35
// vg-binding: PLAN:task-36
// vg-binding: PLAN:task-37
// vg-binding: PLAN:task-38
// vg-binding: P6.D-119
// vg-binding: P6.D-225
// vg-binding: G-63
// vg-binding: P6.D-80
// vg-binding: P6.D-81
// vg-binding: P6.D-82
// vg-binding: P6.D-83
// vg-binding: P6.D-84
// vg-binding: P6.D-86
// vg-binding: P6.D-87
// vg-binding: P6.D-88
// vg-binding: P6.D-89
// vg-binding: P6.D-90
// vg-binding: P6.D-91
// vg-binding: P6.D-122
// vg-binding: P6.D-123
// vg-binding: P6.D-124
// vg-binding: P6.D-125
// vg-binding: P6.D-126
// vg-binding: P6.D-127
// vg-binding: P6.D-128
// vg-binding: P6.D-129
// vg-binding: P6.D-130
// vg-binding: P6.D-131
// vg-binding: P6.D-132
// vg-binding: P6.D-221
// vg-binding: P6.D-249
// vg-binding: G-57
// vg-binding: G-58
// vg-binding: G-59
// vg-binding: G-60
// vg-binding: G-61
// vg-binding: G-69
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape

import { apiClient, ApiError } from '@/lib/api/client';
import type { MeUser } from '@/types/auth';
import type {
  ActivityEntry,
  CreateTeamMemberPayload,
  LoginAttempt,
  LoginHistoryFilters,
  NotificationPrefs,
  PhoneOtpResult,
  Session,
  Setup2FAResponse,
  TeamMember,
  TeamMemberFilters,
  UpdatePreferencesPayload,
  UpdateProfilePayload,
  UpdateTeamMemberPayload,
  Verify2FAResponse,
} from '@/types/settings';

const BASE = '/api/v1/auth/me';

/**
 * PATCH /api/v1/auth/me — update editable profile + billing fields.
 * Returns the canonical envelope `{ ok, user }` so the caller can
 * mirror the fresh user back into the React Query cache + Zustand
 * store without an extra GET.
 */
export async function updateProfile(
  payload: UpdateProfilePayload,
): Promise<{ ok: true; user: MeUser }> {
  // The BE returns the wrapped `{ user }` shape directly; the
  // api/client unwraps the outer success envelope so we receive the
  // inner data object here.
  const data = await apiClient.patch<{ user: MeUser } | MeUser>(BASE, payload);
  // Tolerate either shape — when the BE wraps under `user`, unwrap;
  // when it returns the user object directly, accept that too.
  if (data && typeof data === 'object' && 'user' in data && data.user) {
    return { ok: true, user: data.user };
  }
  return { ok: true, user: data as MeUser };
}

/**
 * POST /api/v1/auth/me/avatar — multipart upload of a new avatar
 * image. The BE persists the file then returns the canonical CDN URL.
 */
export async function uploadAvatar(
  file: File,
): Promise<{ ok: true; avatarUrl: string }> {
  const fd = new FormData();
  fd.append('avatar', file);
  const data = await apiClient.post<{ avatarUrl: string }>(
    `${BASE}/avatar`,
    fd,
  );
  return { ok: true, avatarUrl: data.avatarUrl };
}

/**
 * GET /api/v1/auth/me/activity — paginated security activity timeline
 * (login, password_change, 2fa_enable, profile_edit). 404 / network
 * fallback returns an empty list so the page renders an empty state
 * instead of crashing.
 */
export async function getActivity(): Promise<{ rows: ActivityEntry[] }> {
  try {
    const data = await apiClient.get<
      { rows: ActivityEntry[] } | { activity?: ActivityEntry[] }
    >(`${BASE}/activity`);
    if (
      data &&
      typeof data === 'object' &&
      'rows' in data &&
      Array.isArray((data as { rows: ActivityEntry[] }).rows)
    ) {
      return data as { rows: ActivityEntry[] };
    }
    if (
      data &&
      typeof data === 'object' &&
      'activity' in data &&
      Array.isArray((data as { activity?: ActivityEntry[] }).activity)
    ) {
      return {
        rows: (data as { activity: ActivityEntry[] }).activity,
      };
    }
    return { rows: [] };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [] };
    }
    throw err;
  }
}

/**
 * PATCH /api/v1/auth/me/preferences — persist theme + density + locale
 * + timezone. Returns `{ ok }` on success; the caller is expected to
 * invalidate the `useMe()` query so the fresh values flow through.
 */
export async function updatePreferences(
  payload: UpdatePreferencesPayload,
): Promise<{ ok: true }> {
  await apiClient.patch<unknown>(`${BASE}/preferences`, payload);
  return { ok: true };
}

// --- Phase 6 Wave 11 Task 36 — Security surface --------------------------

/** Payload for `changePassword()` — server validates `currentPassword`
 *  before applying `newPassword`. Both required. */
export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

/**
 * POST /api/v1/auth/change-password — rotate the merchant password
 * . The BE invalidates every active session except the
 * current one and emits `password_change` to the activity timeline.
 * Caller is expected to navigate to /login on success so the user
 * re-authenticates against the new credential (defence-in-depth).
 */
export async function changePassword(
  payload: ChangePasswordPayload,
): Promise<{ ok: true }> {
  // skipAuthRefresh: a wrong current password returns 401
  // (AUTH_INVALID_CREDENTIALS). Without this, the client's global 401
  // handler would treat it as an expired session and bounce the user to
  // /login instead of surfacing the inline "current password incorrect"
  // error. The session itself is still valid here.
  await apiClient.post<unknown>('/api/v1/auth/change-password', payload, {
    skipAuthRefresh: true,
  });
  return { ok: true };
}

/**
 * POST /api/v1/auth/2fa/setup-totp — initiate the TOTP enrollment flow
 * . Returns the base32 `secret` + an `otpauth://` URL the
 * FE renders as a QR code. The secret is NOT persisted as the active
 * 2FA secret until `verifySetup2FA()` is called with the matching
 * enrollment token + 6-digit code.
 */
export async function setup2FA(): Promise<Setup2FAResponse> {
  const data = await apiClient.post<Setup2FAResponse>(
    '/api/v1/auth/2fa/setup-totp',
    { label: 'Printway Merchant' },
  );
  return {
    ...data,
    qrCodeUrl: data.qrCodeUrl ?? data.qrCodeDataUrl,
  };
}

/**
 * POST /api/v1/auth/2fa/verify-totp — verify the pending TOTP enrollment
 * and return one-shot recovery codes.
 */
export async function verifySetup2FA(
  enrollmentToken: string,
  code: string,
): Promise<Verify2FAResponse> {
  return apiClient.post<Verify2FAResponse>('/api/v1/auth/2fa/verify-totp', {
    enrollmentToken,
    code,
  });
}

/**
 * POST /api/v1/auth/2fa/disable — turn off 2FA for the merchant.
 * dbg-20260702 — the endpoint re-confirms the account PASSWORD in the body
 * (API-CONTRACTS P1: `{ password }`, P1.D-44 Tier A). The FE previously sent
 * the 2FA OTP `{ code }`, which failed the BE zod schema → 400 "Bad request
 * body". Send the password so a stolen-cookie attacker who lacks the password
 * cannot strip 2FA off the account.
 */
export async function disable2FA(password: string): Promise<{ ok: true }> {
  await apiClient.post<unknown>('/api/v1/auth/2fa/disable', { password });
  return { ok: true };
}

/**
 * GET /api/v1/auth/sessions — list the currently active sessions for
 * this user. One row per device + browser + remote IP. The
 * row whose `isCurrent` flag is true is the session driving this
 * request. 404 / network failure → empty list so the page renders an
 * empty state instead of crashing (mirrors `getActivity()`).
 */
export async function listSessions(): Promise<Session[]> {
  try {
    const data = await apiClient.get<{ sessions?: Session[] } | Session[]>(
      '/api/v1/auth/sessions',
    );
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'sessions' in data && Array.isArray(data.sessions)) {
      return data.sessions;
    }
    return [];
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return [];
    }
    throw err;
  }
}

/**
 * DELETE /api/v1/auth/sessions/:id — kill a single session by id.
 * UAT #1858 — the client used to POST /sessions/:id/revoke, a route the
 * BE never registered (canonical: DELETE /sessions/:id) → every click
 * errored.
 */
export async function revokeSession(id: string): Promise<{ ok: true }> {
  await apiClient.delete<unknown>(
    `/api/v1/auth/sessions/${encodeURIComponent(id)}`,
  );
  return { ok: true };
}

/**
 * POST /api/v1/auth/sessions/logout-all — kill every session EXCEPT the
 * current one. UAT #1859 — the client used to POST /sessions/revoke-all,
 * a route the BE never registered (canonical: /sessions/logout-all).
 */
export async function revokeAllSessions(): Promise<{ ok: true }> {
  await apiClient.post<unknown>('/api/v1/auth/sessions/logout-all', {});
  return { ok: true };
}

/**
 * GET /api/v1/auth/login-history — paginated list of recent login
 * attempts. Filters narrow the BE query so the response
 * stays small. 404 / network failure → empty list (graceful empty
 * state).
 */
export async function getLoginHistory(
  filters: LoginHistoryFilters = {},
): Promise<{ rows: LoginAttempt[] }> {
  const qs = new URLSearchParams();
  if (filters.status && filters.status !== 'all') {
    qs.set('status', filters.status);
  }
  if (filters.fromDate) qs.set('from', filters.fromDate);
  if (filters.toDate) qs.set('to', filters.toDate);
  const path = qs.toString()
    ? `/api/v1/auth/login-history?${qs.toString()}`
    : '/api/v1/auth/login-history';
  try {
    const data = await apiClient.get<
      { rows?: LoginAttempt[] } | LoginAttempt[]
    >(path);
    if (Array.isArray(data)) return { rows: data };
    if (data && typeof data === 'object' && 'rows' in data && Array.isArray(data.rows)) {
      return { rows: data.rows };
    }
    return { rows: [] };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [] };
    }
    throw err;
  }
}

/**
 * POST /api/v1/auth/forgot-password — request a password-reset email
 * . Returns success even if the email is not registered so
 * the endpoint cannot be used for account enumeration.
 */
export async function forgotPassword(
  email: string,
): Promise<{ ok: true }> {
  await apiClient.post<unknown>('/api/v1/auth/forgot-password', { email });
  return { ok: true };
}

// --- Phase 6 Wave 11 Task 37 — Team Members ------------------------------

const TEAM_BASE = '/api/v1/team-members';

/**
 * GET /api/v1/team-members — paginated list of merchant team members
 * . Filter by role + status + free-text search. 404 / network
 * failure → empty page so the table renders an empty state instead of
 * crashing (mirrors `getActivity()`).
 *
 * Tolerates two BE response shapes for forward-compatibility:
 *   - `{ rows: TeamMember[]; total: number }` (canonical)
 *   - `{ teamMembers: TeamMember[] }` (legacy admin shape — `total`
 *     defaults to `rows.length` so the pager still renders)
 */
export async function listTeamMembers(
  filters: TeamMemberFilters = {},
): Promise<{ rows: TeamMember[]; total: number }> {
  const qs = new URLSearchParams();
  if (filters.role && filters.role !== 'all') qs.set('role', filters.role);
  if (filters.status && filters.status !== 'all') {
    qs.set('status', filters.status);
  }
  if (filters.search && filters.search.trim()) {
    qs.set('search', filters.search.trim());
  }
  const path = qs.toString() ? `${TEAM_BASE}?${qs.toString()}` : TEAM_BASE;
  try {
    const data = await apiClient.get<
      | { rows: TeamMember[]; total?: number }
      | { teamMembers: TeamMember[]; total?: number }
      | TeamMember[]
    >(path);
    if (Array.isArray(data)) {
      return { rows: data, total: data.length };
    }
    if (data && typeof data === 'object' && 'rows' in data && Array.isArray(data.rows)) {
      return {
        rows: data.rows,
        total: typeof data.total === 'number' ? data.total : data.rows.length,
      };
    }
    if (
      data &&
      typeof data === 'object' &&
      'teamMembers' in data &&
      Array.isArray((data as { teamMembers: TeamMember[] }).teamMembers)
    ) {
      const rows = (data as { teamMembers: TeamMember[] }).teamMembers;
      const total = (data as { total?: number }).total;
      return { rows, total: typeof total === 'number' ? total : rows.length };
    }
    return { rows: [], total: 0 };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [], total: 0 };
    }
    throw err;
  }
}

/**
 * POST /api/v1/team-members — invite a new team member (
 * The BE creates the record in `pending_invite`
 * and (when `sendInvite !== false`) emails the activation link.
 */
export async function createTeamMember(
  payload: CreateTeamMemberPayload,
): Promise<{ ok: true; teamMember: TeamMember }> {
  const data = await apiClient.post<{ teamMember: TeamMember } | TeamMember>(
    TEAM_BASE,
    payload,
  );
  if (data && typeof data === 'object' && 'teamMember' in data && data.teamMember) {
    return { ok: true, teamMember: data.teamMember };
  }
  return { ok: true, teamMember: data as TeamMember };
}

/**
 * PATCH /api/v1/team-members/:id — partial update (name / role /
 * monthlyBudget / orderLimit). Email is immutable post-invite
 *  and the BE rejects an `email` field with a 400.
 *
 * Sending `null` for `monthlyBudget` / `orderLimit` clears the cap
 * (the BE unsets the field). Omitting the field leaves it unchanged.
 */
export async function updateTeamMember(
  id: string,
  payload: UpdateTeamMemberPayload,
): Promise<{ ok: true; teamMember: TeamMember }> {
  const data = await apiClient.patch<
    { teamMember: TeamMember } | TeamMember
  >(`${TEAM_BASE}/${encodeURIComponent(id)}`, payload);
  if (data && typeof data === 'object' && 'teamMember' in data && data.teamMember) {
    return { ok: true, teamMember: data.teamMember };
  }
  return { ok: true, teamMember: data as TeamMember };
}

/**
 * POST /api/v1/team-members/:id/suspend — reversible pause.
 * The BE invalidates active sessions for the suspended member but
 * keeps the record so a later `reactivateTeamMember()` restores
 * access. Reason is required (audit-trail).
 */
export async function suspendTeamMember(
  id: string,
  reason: string,
): Promise<{ ok: true }> {
  await apiClient.post<unknown>(
    `${TEAM_BASE}/${encodeURIComponent(id)}/suspend`,
    { reason },
  );
  return { ok: true };
}

/**
 * POST /api/v1/team-members/:id/deactivate — irreversible offboard
 * . The BE revokes every session and marks the record so
 * the merchant cannot reactivate the user from the UI; record is
 * kept for audit. Reason is required (audit-trail).
 */
export async function deactivateTeamMember(
  id: string,
  reason: string,
): Promise<{ ok: true }> {
  await apiClient.post<unknown>(
    `${TEAM_BASE}/${encodeURIComponent(id)}/deactivate`,
    { reason },
  );
  return { ok: true };
}

/**
 * POST /api/v1/team-members/:id/reactivate — restore a suspended
 * member. The BE flips status `suspended → active` and
 * clears `suspendReason`. Returns 409 if the member is currently
 * deactivated (irreversible).
 */
export async function reactivateTeamMember(
  id: string,
): Promise<{ ok: true }> {
  await apiClient.post<unknown>(
    `${TEAM_BASE}/${encodeURIComponent(id)}/reactivate`,
    {},
  );
  return { ok: true };
}

/**
 * POST /api/v1/team-members/:id/resend-invite — re-send the activation
 * email for a `pending_invite` member. The BE rotates the
 * invite token so any prior link is invalidated. Returns 409 if the
 * member has already accepted (status !== `pending_invite`).
 */
export async function resendInvite(id: string): Promise<{ ok: true }> {
  await apiClient.post<unknown>(
    `${TEAM_BASE}/${encodeURIComponent(id)}/resend-invite`,
    {},
  );
  return { ok: true };
}

// --- Phase 6 Wave 11 Task 38 — Notification preferences -----------------

/**
 * GET /api/v1/auth/me/notification-prefs — R8-2 (2026-05-17, BUG-P6-DEBUG-B1).
 * Reads persisted dense matrix so the page can hydrate server-truth on
 * mount instead of starting from hardcoded defaults. 404 / network
 * failure → returns null and lets the caller fall back to its in-FE
 * default (mirrors the wallet/activity tolerance pattern).
 */
export async function getNotificationPrefs(): Promise<NotificationPrefs | null> {
  try {
    const data = await apiClient.get<
      | { notificationPrefs: NotificationPrefs }
      | { prefs: NotificationPrefs }
      | NotificationPrefs
    >(`${BASE}/notification-prefs`);
    if (
      data &&
      typeof data === 'object' &&
      'notificationPrefs' in data &&
      data.notificationPrefs
    ) {
      return data.notificationPrefs;
    }
    if (data && typeof data === 'object' && 'prefs' in data && data.prefs) {
      return data.prefs;
    }
    if (
      data &&
      typeof data === 'object' &&
      'categories' in (data as Record<string, unknown>)
    ) {
      return data as NotificationPrefs;
    }
    return null;
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return null;
    }
    throw err;
  }
}

/**
 * PATCH /api/v1/auth/me/notification-prefs — persist the dense
 * category × channel toggle map + in-app sound flag (
 * Tolerates three BE response shapes for forward-compatibility:
 *   - `{ ok, notificationPrefs }` (canonical Phase 6 Wave 13 envelope — current BE)
 *   - `{ ok, prefs }` (mid-wave alias)
 *   - bare prefs object (legacy shape — wrapped before return)
 */
export async function updateNotificationPrefs(
  payload: NotificationPrefs,
): Promise<{ ok: true; prefs: NotificationPrefs }> {
  const data = await apiClient.patch<
    | { notificationPrefs: NotificationPrefs }
    | { prefs: NotificationPrefs }
    | NotificationPrefs
  >(`${BASE}/notification-prefs`, payload);
  if (
    data &&
    typeof data === 'object' &&
    'notificationPrefs' in data &&
    data.notificationPrefs
  ) {
    return { ok: true, prefs: data.notificationPrefs };
  }
  if (data && typeof data === 'object' && 'prefs' in data && data.prefs) {
    return { ok: true, prefs: data.prefs };
  }
  // Legacy bare-prefs fallback — defensive: ensure shape has categories
  // before treating as full prefs; otherwise echo the FE's payload to
  // avoid crashing the form.
  if (
    data &&
    typeof data === 'object' &&
    'categories' in (data as Record<string, unknown>)
  ) {
    return { ok: true, prefs: data as NotificationPrefs };
  }
  return { ok: true, prefs: payload };
}

// --- Phase 6.1 Wave 5 Task 21 — Members (new /api/v1/members surface) -----
//
// Replaces the legacy `/api/v1/team-members` list for the renamed
// "Members" page (P6.1.D-25). Kept side-by-side with TEAM_BASE above so
// the legacy TeamMembersPage stays importable during the redirect window
// (rollback-safe). Endpoints per API-CONTRACTS get-api-v1-members +
// post-api-v1-members-id-toggle + post-api-v1-members-invite.
//
// vg-binding: PLAN:task-21
// vg-binding: P6.1.D-25
// vg-binding: P6.1.D-08
// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:response-envelope

const MEMBER_BASE = '/api/v1/members';

/** Wire role enum — English VALUE (label localized in the FE). */
export type MemberRole = 'owner' | 'admin' | 'manager' | 'staff' | 'viewer';
/** Wire status enum mapped to `TeamMember.status` (D-08). */
export type MemberStatus = 'invited' | 'active' | 'disabled';

/** One row of GET /api/v1/members (contract MemberListItem). */
export interface MemberListItem {
  id: string;
  userId: string;
  displayName: string | null;
  email: string;
  role: MemberRole;
  teamId: string | null;
  teamName: string | null;
  status: MemberStatus;
  createdAt: number; // unix ms
  updatedAt: number; // unix ms
}

/** meta block of GET /api/v1/members (offset pagination + manager scope). */
export interface MembersMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  scopedToTeamId: string | null;
}

/** Filters → GET /api/v1/members query (kebab url params per config). */
export interface MembersFilters {
  q?: string;
  role?: MemberRole;
  team?: string; // teamId
  status?: MemberStatus;
  page?: number;
  pageSize?: number;
  sort?: string;
  /** P6.1 (G-31) — restrict to members whose team owns any of these stores
   *  (real store ids). Serialized as repeated/CSV `store-ids` query param. */
  storeIds?: string[];
}

const EMPTY_MEMBERS_META: MembersMeta = {
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 1,
  scopedToTeamId: null,
};

/**
 * GET /api/v1/members — list members with search/filter/pagination.
 * Uses `getEnvelope` so the caller can read `meta` (offset pagination +
 * `scopedToTeamId` for manager transparency, D-02). 404 / network failure
 * → empty page so the table renders an empty state instead of crashing
 * (mirrors `getActivity()`).
 */
export async function listMembers(
  filters: MembersFilters = {},
): Promise<{ rows: MemberListItem[]; meta: MembersMeta }> {
  const qs = new URLSearchParams();
  const q = filters.q?.trim();
  if (q) qs.set('q', q);
  if (filters.role) qs.set('role', filters.role);
  if (filters.team) qs.set('team', filters.team);
  if (filters.status) qs.set('status', filters.status);
  if (filters.page && filters.page > 1) qs.set('page', String(filters.page));
  if (filters.pageSize) qs.set('page-size', String(filters.pageSize));
  if (filters.sort) qs.set('sort', filters.sort);
  // P6.1 (G-31) — CSV of real store ids; BE resolves to teams owning them.
  if (filters.storeIds && filters.storeIds.length > 0) {
    qs.set('store-ids', filters.storeIds.join(','));
  }
  const path = qs.toString() ? `${MEMBER_BASE}?${qs.toString()}` : MEMBER_BASE;
  try {
    const env = await apiClient.getEnvelope<MemberListItem[]>(path);
    const rows = Array.isArray(env.data) ? env.data : [];
    const meta = (env.meta as Partial<MembersMeta> | undefined) ?? {};
    return {
      rows,
      meta: {
        page: meta.page ?? 1,
        pageSize: meta.pageSize ?? filters.pageSize ?? 20,
        total: meta.total ?? rows.length,
        totalPages: meta.totalPages ?? 1,
        scopedToTeamId: meta.scopedToTeamId ?? null,
      },
    };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [], meta: EMPTY_MEMBERS_META };
    }
    throw err;
  }
}

/** Response of POST /api/v1/members/:id/toggle (desired-state, D-08). */
export interface ToggleMemberResult {
  id: string;
  status: 'active' | 'disabled';
  scopeBlocked: boolean;
  scopeVersionBumped: boolean;
  updatedAt: number;
}

/**
 * POST /api/v1/members/:id/toggle — binary Active↔Deactive toggle mapped
 * to `TeamMember.status`. Body `{ active }` is the DESIRED state
 * (idempotent, double-click-safe — D-08). Toggling an `invited` member →
 * 409 MEMBER_INVITED (FE prevents by disabling the switch). Surfaces
 * ApiError so the caller can rollback the optimistic flip + toast the
 * API message (INTERFACE-STANDARDS message priority).
 */
export async function toggleMember(
  id: string,
  active: boolean,
): Promise<ToggleMemberResult> {
  return apiClient.post<ToggleMemberResult>(
    `${MEMBER_BASE}/${encodeURIComponent(id)}/toggle`,
    { active },
  );
}

/** Payload for PATCH /api/v1/members/:id (BE PatchMemberBody — at least one). */
export interface PatchMemberPayload {
  role?: 'admin' | 'manager' | 'staff' | 'viewer';
  teamId?: string | null;
  displayName?: string;
  monthlyBudgetUsdCents?: number | null;
  perOrderLimitUsdCents?: number | null;
}

/**
 * PATCH /api/v1/members/:id — edit a member's role / team / limits.
 * G-14: promoting to `role:'admin'` is owner-only server-side → non-owner
 * gets 403 `OWNER_ONLY_PROMOTE` (ApiError surfaced for toast + rollback).
 */
export async function patchMember(
  id: string,
  payload: PatchMemberPayload,
): Promise<{ member: MemberListItem }> {
  return apiClient.patch<{ member: MemberListItem }>(
    `${MEMBER_BASE}/${encodeURIComponent(id)}`,
    payload,
  );
}

/** Payload for POST /api/v1/members/invite (contract InviteBody). */
export interface InviteMemberPayload {
  email: string;
  fullName: string;
  role: 'admin' | 'manager' | 'staff' | 'viewer'; // 'owner' NOT invitable
  teamId?: string | null; // null = no-team invite
  monthlyBudgetUsdCents?: number | null;
  perOrderLimitUsdCents?: number | null;
}

/** Response data of POST /api/v1/members/invite (status='invited'). */
export interface InviteMemberResult {
  id: string;
  email: string;
  role: 'admin' | 'manager' | 'staff' | 'viewer';
  teamId: string | null;
  teamName: string | null;
  status: 'invited';
  invitedAt: number;
}

/**
 * POST /api/v1/members/invite — invite a new member. Non-optimistic: wait
 * for the server id then refetch (per FE consumer contract). 409
 * MEMBER_ALREADY_EXISTS / 403 OWNER_ONLY_PROMOTE surface as ApiError so
 * the modal can bind `field_errors.email` or toast the message.
 */
export async function inviteMember(
  payload: InviteMemberPayload,
): Promise<InviteMemberResult> {
  return apiClient.post<InviteMemberResult>(
    `${MEMBER_BASE}/invite`,
    payload,
  );
}

/** Minimal team option for the Members filter + invite team select. */
export interface MemberTeamOption {
  id: string;
  name: string;
}

/**
 * GET /api/v1/teams — thin projection to `{ id, name }[]` for the Team
 * filter dropdown + invite modal. Full TeamsPage client lands in task-23;
 * here we only need the names. 404 / network failure → empty list so the
 * Team filter simply shows "All".
 */
export async function listMemberTeams(): Promise<MemberTeamOption[]> {
  try {
    const data = await apiClient.get<Array<{ id: string; name: string }>>(
      '/api/v1/teams?page-size=100&sort=name',
    );
    if (!Array.isArray(data)) return [];
    return data.map((t) => ({ id: t.id, name: t.name }));
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return [];
    }
    throw err;
  }
}

// --- 2026-06-26 — legacy account-settings parity ------------------------

/**
 * POST /api/v1/auth/me/phone/send-otp — send a 6-digit verification code
 * to the user's current phone number so SMS notifications can be enabled
 * (legacy SMS "Verify Phone" flow).
 */
export async function sendPhoneOtp(): Promise<PhoneOtpResult> {
  await apiClient.post<unknown>(`${BASE}/phone/send-otp`, {});
  return { ok: true };
}

/**
 * POST /api/v1/auth/me/phone/verify-otp — submit the 6-digit code; on a
 * match the BE sets `phoneVerified = true` so SMS notifications unlock.
 */
export async function verifyPhoneOtp(code: string): Promise<PhoneOtpResult> {
  await apiClient.post<unknown>(`${BASE}/phone/verify-otp`, { code });
  return { ok: true, verified: true };
}

/**
 * POST /api/v1/auth/me/notification-prefs/test-email — send a one-off test
 * email to the user (legacy Email Notifications "Test" button).
 */
export async function testNotificationEmail(): Promise<{ ok: true }> {
  await apiClient.post<unknown>(`${BASE}/notification-prefs/test-email`, {});
  return { ok: true };
}
